import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';

const caseStudies = [
  {
    id: 'afil',
    title: 'Advanced Food Intolerance Labs',
    category: 'Health & Wellness',
    description: 'Multi-channel marketing strategy that drove $172K+ in sales with a 44% conversion rate boost.',
    metrics: [
      { value: '+44%', label: 'Conversion Rate' },
      { value: '$172K+', label: 'Total Sales' },
      { value: '2.64x', label: 'ROAS' },
    ],
    image: 'https://images.unsplash.com/photo-1535914254981-b5012eebbd15?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
    link: '/work/afil',
    services: ['Social Media', 'Affiliate Marketing', 'Ads Management'],
  },
  {
    id: 'mommas',
    title: "MoMMAs Voices",
    category: 'Nonprofit / Healthcare',
    description: 'Email marketing campaign for maternal health advocacy that achieved 37% open rates.',
    metrics: [
      { value: '37%', label: 'Open Rate' },
      { value: '180', label: 'Event Registrations' },
      { value: '3,176', label: 'Emails Sent' },
    ],
    image: 'https://kajabi-storefronts-production.kajabi-cdn.com/kajabi-storefronts-production/file-uploads/themes/2153811776/settings_images/0f820ee-6e52-58bd-066b-5c6148844c4_MV_Website_Graphic_3_.png',
    link: '/work/mommas-voices',
    services: ['Email Marketing', 'Content Strategy'],
  },
];

export const WorkPage = () => {
  return (
    <div className="min-h-screen pt-20" data-testid="work-page">
      {/* Hero Section */}
      <section className="section-padding bg-forest" data-testid="work-hero">
        <div className="container-custom">
          <motion.div
            className="max-w-4xl"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-gold font-medium tracking-widest uppercase text-sm mb-6 block">
              Our Work
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-cream leading-[1.1] mb-6">
              Case Studies &{' '}
              <span className="text-gold">Success Stories</span>
            </h1>
            <p className="text-xl text-cream/80 max-w-2xl leading-relaxed">
              Real results for real brands. Explore how we've helped businesses achieve 
              measurable growth through data-driven digital marketing.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Case Studies Grid */}
      <section className="section-padding bg-cream" data-testid="work-grid">
        <div className="container-custom">
          <div className="space-y-24">
            {caseStudies.map((study, index) => (
              <motion.div
                key={study.id}
                className={`grid lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? '' : ''
                }`}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                data-testid={`case-study-card-${study.id}`}
              >
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <Link to={study.link} className="block group">
                    <div className="relative overflow-hidden rounded-2xl aspect-[4/3]">
                      <img 
                        src={study.image} 
                        alt={study.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-forest-dark/20 group-hover:bg-forest-dark/10 transition-colors" />
                    </div>
                  </Link>
                </div>

                <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                  <span className="text-gold font-medium tracking-widest uppercase text-sm mb-4 block">
                    {study.category}
                  </span>
                  <h2 className="text-3xl md:text-4xl font-bold text-forest mb-4">
                    {study.title}
                  </h2>
                  <p className="text-lg text-forest/70 mb-8 leading-relaxed">
                    {study.description}
                  </p>

                  {/* Metrics */}
                  <div className="grid grid-cols-3 gap-4 mb-8">
                    {study.metrics.map((metric, i) => (
                      <div key={i} className="text-center p-4 bg-cream-paper rounded-xl border border-forest/10">
                        <p className="text-2xl md:text-3xl font-bold text-gold">{metric.value}</p>
                        <p className="text-sm text-forest/60 mt-1">{metric.label}</p>
                      </div>
                    ))}
                  </div>

                  {/* Services Tags */}
                  <div className="flex flex-wrap gap-2 mb-8">
                    {study.services.map((service) => (
                      <span
                        key={service}
                        className="bg-forest/10 text-forest px-4 py-2 rounded-full text-sm font-medium"
                      >
                        {service}
                      </span>
                    ))}
                  </div>

                  <Link to={study.link} data-testid={`case-study-link-${study.id}`}>
                    <Button variant="outline" className="border-2 border-forest text-forest hover:bg-forest hover:text-cream rounded-full px-8 py-6 text-lg">
                      View Case Study
                      <ArrowRight className="ml-2" size={20} />
                    </Button>
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-gold" data-testid="work-cta">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-forest-dark mb-6">
              Ready to Be Our Next Success Story?
            </h2>
            <p className="text-xl text-forest-dark/70 mb-10 max-w-2xl mx-auto">
              Let's discuss how we can help you achieve similar results for your brand.
            </p>
            <Link to="/contact" data-testid="work-cta-button">
              <Button className="bg-forest text-cream hover:bg-forest-dark rounded-full px-10 py-6 text-lg font-semibold">
                Start Your Project
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default WorkPage;
